import React, { createContext, useContext, useReducer, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import Cookies from 'js-cookie';
import apiService from '../services/apiService';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  permissions: {},
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        permissions: {},
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_PERMISSIONS':
      return {
        ...state,
        permissions: action.payload,
      };
    default:
      return state;
  }
};
// console.log("ignore")
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const token = Cookies.get('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 > Date.now()) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: decodedToken.user,
              token,
            },
          });
        } else {
          Cookies.remove('token');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        Cookies.remove('token');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const login = async (credentials, loginType = 'login') => {
    try {      
      let response;
      switch (loginType) {
        case 'admin':
          response = await apiService.adminLogin(credentials);
          break;
        case 'staff':
          response = await apiService.staffLogin(credentials);
          break;
        default:
          response = await apiService.login(credentials);
      }

      if (response.status && response.token) {
        const decodedToken = jwtDecode(response.token);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: decodedToken.user,
            token: response.token,
          },
        });
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    const userRole = state.user?.role;
    apiService.logout();
    dispatch({ type: 'LOGOUT' });
    
    // Return redirect path based on role
    if (userRole === 'admin') {
      return '/super-admin-login';
    } else if (userRole === 'staff') {
      return '/login'; // Staff will be redirected from login page
    }
    return '/login';
  };

  // Permission checking utility matching Laravel Helper::hasPermission
  const hasPermission = (page, module, action) => {
    if (!state.user) return false;
    
    // Admin has all permissions
    if (state.user.role === 'admin') return true;
    
    // Only staff uses permissions, and it must be set and not empty
    if (state.user.role === 'staff' && state.user.permissions && state.user.permissions.length > 0) {
      for (const permission of state.user.permissions) {
        if (permission.page === page) {
          const modules = permission.modules || [];
          for (const mod of modules) {
            if (mod.name === module && mod.actions && mod.actions.includes(action)) {
              return true;
            }
          }
        }
      }
    }
    
    return false;
  };

  // Role-based access control
  const hasRole = (roles) => {
    if (!state.user) return false;
    const userRole = state.user.role;
    return Array.isArray(roles) ? roles.includes(userRole) : roles === userRole;
  };

  // Service access control
  const hasService = (service) => {
    if (!state.user) return false;
    if (state.user.role === 'admin') return true;
    return state.user.services?.includes(service) || false;
  };

  const value = {
    ...state,
    login,
    logout,
    hasPermission,
    hasRole,
    hasService,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};